"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactElement, ReactNode } from "react";
import { cloneElement, isValidElement, useState } from "react";
import { cn } from "../../lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = ({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) => (
  <TooltipPrimitive.Content
    className={cn(
      "fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 z-50 animate-in overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-popover-foreground text-sm shadow-md data-[state=closed]:animate-out",
      className
    )}
    sideOffset={sideOffset}
    {...props}
  />
);

type TooltipProps = {
  children: ReactNode;
  content: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delayDuration?: number;
  className?: string;
};

const DEFAULT_DELAY_DURATION = 200;

export function Tooltip(props: Readonly<TooltipProps>): ReactNode {
  const [open, setOpen] = useState<boolean | undefined>(undefined);

  // If children is a valid React element, clone it to add click/tap handlers
  // so the tooltip can be opened on touch devices as well as hover.
  const trigger = isValidElement(props.children)
    ? cloneElement(
        props.children as ReactElement<Record<string, unknown>>,
        {
          onClick: (e: React.MouseEvent) => {
            // preserve existing handler
            const childProps = (
              props.children as ReactElement<Record<string, unknown>>
            ).props;
            const origOnClick = childProps.onClick as
              | ((e: React.MouseEvent) => void)
              | undefined;
            origOnClick?.(e);
            setOpen((v) => !v);
          },
          onTouchStart: (e: React.TouchEvent) => {
            const childProps = (
              props.children as ReactElement<Record<string, unknown>>
            ).props;
            const origOnTouchStart = childProps.onTouchStart as
              | ((e: React.TouchEvent) => void)
              | undefined;
            origOnTouchStart?.(e);
            setOpen((v) => !v);
          },
        } as Record<string, unknown>
      )
    : props.children;

  return (
    <TooltipProvider
      delayDuration={props.delayDuration ?? DEFAULT_DELAY_DURATION}
    >
      <TooltipRoot onOpenChange={setOpen} open={open}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          align={props.align ?? "center"}
          className={props.className}
          side={props.side ?? "top"}
        >
          {props.content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}
